import axios from "axios";
import { toast } from "sonner";
import { logger } from "./logger";
// Empty (the default) means same-origin: requests go to "/api" and the dev server proxies them to
// the backend (see craco.config.js). That is what lets the app work over a tunnel or from a phone —
// an absolute URL here has to be reachable by the *browser*, not by this machine.
const BACKEND = process.env.REACT_APP_BACKEND_URL || "";
export const API = `${BACKEND}/api`;

export const api = axios.create({
  baseURL: API,
  withCredentials: true,
});

const log = logger.child("api");

// attach Bearer token from localStorage as fallback (cookies may fail in cross-site)
api.interceptors.request.use((cfg) => {
  const tok = localStorage.getItem("dt_token");
  if (tok) cfg.headers.Authorization = `Bearer ${tok}`;
  // Start the clock here so the response interceptor can report how long the call actually took
  // from the browser's point of view — which includes the network, and so is the number that
  // matches what the user felt. The server's own duration is in its log under the same id.
  cfg.metadata = { started: Date.now() };
  log.debug(`--> ${String(cfg.method || "get").toUpperCase()} ${cfg.url}`);
  return cfg;
});

// Where a role belongs when its session turns out to be wrong for the area it is in.
const LOGIN_FOR = { "/admin": "/admin/login", "/advocate": "/advocate/login" };
const loginPathFor = (path) =>
  Object.entries(LOGIN_FOR).find(([prefix]) => path.startsWith(prefix))?.[1] || "/login";

/**
 * Send the user back to sign in when their session is genuinely gone.
 *
 * **401 only.** 401 means "no valid session" — nothing works until they sign in again, so
 * clearing the token and redirecting is the only useful thing to do.
 *
 * 403 is deliberately left alone. It means "valid session, not allowed", which is an ordinary
 * outcome of the permission rules here: opening a transaction that isn't yours, or a reviewer
 * report marked internal, both return 403 by design. Treating those as a broken session logged
 * the user out and reloaded the page over a single forbidden link — far worse than the error it
 * was meant to tidy up. A 403 is the caller's to handle, and every data load now has a .catch.
 */
api.interceptors.response.use(
  (res) => {
    // `x-request-id` is the server's correlation id for this exact call (see
    // backend/server.py::request_context_mw). Logging it here is what lets a browser console and
    // the backend log be joined on one value — paste it into Kibana and the whole request is there.
    // Readable cross-origin only because the API sends `expose_headers`.
    const ms = Date.now() - (res.config?.metadata?.started || Date.now());
    log.debug(`<-- ${res.status} ${res.config?.url} in ${ms}ms`, {
      request_id: res.headers?.["x-request-id"],
      status: res.status,
      duration_ms: ms,
    });
    return res;
  },
  (err) => {
    const status = err?.response?.status;
    const url = err?.config?.url || "";
    const path = window.location.pathname;
    const ms = Date.now() - (err?.config?.metadata?.started || Date.now());
    const requestId = err?.response?.headers?.["x-request-id"];
    // A failed API call is the single most useful thing this app can log: it is the moment the
    // user's experience broke. `error` for a server fault, `warn` for the ordinary 4xx outcomes
    // the UI is expected to handle (a 403 on someone else's case, a 401 from the session probe),
    // so filtering the console to errors leaves only genuine faults.
    // /auth/me 401s routinely — it is how the app asks "is anyone signed in?"
    // /auth/session likewise: /auth/complete calls it to pick up a redirect sign-in, and handles a
    // 401 itself (back to /login with a reason) rather than via the "session expired" path below.
    const isAuthProbe = url.includes("/auth/me") || url.includes("/auth/login") || url.includes("/auth/social")
      || url.includes("/auth/session");
    // A 401 from the session probe is the answer "nobody is signed in", not a fault, and it
    // happens on every mount of a signed-out page — at `warn` it would bury every real error.
    const level =
      isAuthProbe && status === 401 ? "debug" : !err?.response || status >= 500 ? "error" : "warn";
    log[level](`API ${status || "network error"} on ${url}`, {
      request_id: requestId,
      status,
      duration_ms: ms,
      detail: err?.response?.data?.detail,
    });
    if (status === 401 && !isAuthProbe) {
      const target = loginPathFor(path);
      if (path !== target) {
        localStorage.removeItem("dt_token");
        toast.error("Your session has expired — please sign in again.");
        // replace, not push: the page they could not load should not be a back-button trap
        window.location.replace(target);
      }
    }
    return Promise.reject(err);
  },
);

// How many rows to request per page, and the most pages `fetchAll` will ever walk. The ceiling is
// a runaway guard, not a policy: at 500 rows a page it allows 25,000 rows, well past anything this
// application holds today, and it logs rather than failing silently if it is ever hit.
const PAGE_SIZE = 500;
const MAX_PAGES = 50;

/**
 * GET every page of a paginated list endpoint and return one combined array.
 *
 * The list routes return a plain JSON array plus `X-Total-Count` / `X-Has-More` / `X-Next-Cursor`
 * headers (see `backend/core/pagination.py`). Before this existed they returned a fixed first 1000
 * rows and said nothing about the remainder — so once the admin queue passed that, cases simply
 * stopped appearing, with no error and no way to ask for the rest.
 *
 * Paging follows the **cursor**, not a page number. These lists are newest-first and written to
 * constantly; with an offset, a row inserted mid-walk shifts the rest down and gets served twice
 * (or a delete drops one). The cursor names the row to continue after, which no concurrent write
 * can perturb.
 *
 * This keeps the calling pages unchanged: they still receive the whole array and do their own
 * filtering, searching and queue counts client-side. If these lists ever grow large enough that
 * holding them in the browser is the problem, the fix is to move that computation to the server —
 * a feature, not a bug fix, and this loop is what makes the current behaviour honest until then.
 *
 * Falls back to whatever the first response held if the headers are missing (e.g. an old server,
 * or a cross-origin response that didn't expose them), so a missing header degrades to today's
 * behaviour rather than an empty page.
 */
export async function fetchAll(path, config = {}) {
  const out = [];
  let cursor;
  for (let page = 0; page < MAX_PAGES; page++) {
    const params = { ...(config.params || {}), limit: PAGE_SIZE };
    if (cursor) params.cursor = cursor;
    const res = await api.get(path, { ...config, params });

    const batch = Array.isArray(res.data) ? res.data : [];
    out.push(...batch);

    const hasMore = res.headers?.["x-has-more"];
    if (hasMore === undefined) return out;      // unpaginated server — take what we got
    if (hasMore !== "true") return out;
    const next = res.headers?.["x-next-cursor"];
    if (!next || next === cursor) return out;   // no cursor, or it didn't advance: stop rather than loop
    cursor = next;

    if (page === MAX_PAGES - 1) {
      const total = res.headers?.["x-total-count"];
      log.warn(`fetchAll(${path}) stopped at ${out.length} of ${total} rows (page ceiling).`, {
        rows: out.length,
        total,
      });
    }
  }
  return out;
}

export function formatError(detail) {
  if (detail == null) return "Something went wrong.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e?.msg ? e.msg : JSON.stringify(e))).join(" ");
  if (detail?.msg) return detail.msg;
  return String(detail);
}
